import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Flex, HelpIndicator, InfoBox, Text, toast } from "@jsq/ds/core";
import { Form, FormSelect, FormSwitch, useFormContext } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import {
  useEnableComplianceMutation,
  useGetProgramsQuery,
  useSetComplianceProgramMutation,
  useSetFatcaCrsDataCollectionMutation,
} from "~/graphql/supergraphHooks";
import { toGlobalId } from "~/utils/graphql-helpers";
import { ChangeProgramsModal, ProgramChangeRow } from "../ChangeProgramsModal";
import * as drawerStyles from "../styles_vanilla.css";
import { SettingsDrawerRow, SettingsDrawerTabProps } from "../types";

const NONE = "None";
const LEGACY_KYC = "Legacy KYC";
// Payments (CRB) is not offered on the settings dropdowns (it is not an onboarding program)
const EXCLUDED_PROGRAMS = ["Payments (CRB)"];

const LEGACY_KYC_ALERT_TITLE = "Legacy KYC is enabled for this entity";
const LEGACY_KYC_ALERT =
  "This program is no longer offered and is maintained for legacy support only. " +
  "If you transition to a new compliance program, legacy KYC cannot be reactivated.";
const LEGACY_KYC_WARNING =
  "Legacy KYC cannot be reactivated once it has been changed to a new compliance program.";
const SUBSCRIPTIONS_DISABLED_TOOLTIP =
  "Subscription integration cannot be enabled until a compliance program has been specified.";
const SUBSCRIPTIONS_LEGACY_KYC_TOOLTIP =
  "Subscriptions integration is not available for Legacy KYC entities.";

/** The unassigned initial value ("") and the None option both mean "no program". */
const isNoProgram = (program: string) => !program || program === NONE;

/** A legacy entity is on legacy KYC only until a compliance program is chosen for it.
 *
 * isInvestorDiligenceEnabled alone is not enough: the slice keeps it true until the program
 * is both assigned AND enabled. COMP-5288 auto-enables Subscriptions integration, but only
 * on the no-program to program transition, and a legacy entity does not start with no
 * program. So changing Legacy KYC to a real program still leaves the flag set (COMP-5086). */
const isOnLegacyKyc = (row: SettingsDrawerRow) =>
  Boolean(row.isInvestorDiligenceEnabled && !row.complianceProgram);

const SubscriptionsToggle = ({
  disabled,
  tooltipContent,
}: {
  disabled: boolean;
  tooltipContent?: string;
}) => {
  const toggle = (
    <FormSwitch
      name="subscriptionsEnabled"
      label="Enabled"
      uncheckedLabel="Disabled"
      disabled={disabled}
      data-testid="subscriptions-enabled-switch"
    />
  );
  if (!disabled) return toggle;
  return (
    <HelpIndicator
      id="subscriptions-integration-disabled-tooltip"
      placement="left"
      content={tooltipContent ?? SUBSCRIPTIONS_DISABLED_TOOLTIP}
    >
      <span
        className={drawerStyles.disabledToggleTooltipAnchor}
        data-testid="subscriptions-toggle-tooltip-anchor"
      >
        {toggle}
      </span>
    </HelpIndicator>
  );
};

type FormValues = {
  program: string;
  subscriptionsEnabled: boolean;
  fatcaCrsEnabled: boolean;
};

type TabFormProps = SettingsDrawerTabProps & {
  programOptions: string[];
  initialValues: FormValues;
};

const InvestorProgramForm = ({
  row,
  onDirtyChange,
  onSaved,
  onCancel,
  programOptions,
  initialValues,
}: TabFormProps) => {
  const { values, dirty, setFieldValue } = useFormContext<FormValues>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const previousProgram = useRef(values.program);

  // Report dirtiness so the shell can guard close with Exit-and-discard
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    const hadNoProgram = isNoProgram(previousProgram.current);
    const hasProgram = !isNoProgram(values.program);
    const isLegacyKyc = values.program === LEGACY_KYC;
    previousProgram.current = values.program;

    if (isLegacyKyc && values.subscriptionsEnabled) {
      void setFieldValue("subscriptionsEnabled", false);
    } else if (hadNoProgram && hasProgram && !isLegacyKyc && !values.subscriptionsEnabled) {
      void setFieldValue("subscriptionsEnabled", true);
    } else if (!hasProgram && values.subscriptionsEnabled) {
      void setFieldValue("subscriptionsEnabled", false);
    }
  }, [values.program, values.subscriptionsEnabled, setFieldValue]);

  const [setComplianceProgram] = useSetComplianceProgramMutation({
    onError: (e) => toast.error(e.message),
  });
  const [enableCompliance] = useEnableComplianceMutation({
    onError: (e) => toast.error(e.message),
  });
  const [setFatcaCrsDataCollection] = useSetFatcaCrsDataCollectionMutation({
    onError: (e) => toast.error(e.message),
  });

  const programChanged =
    values.program !== initialValues.program &&
    !(isNoProgram(values.program) && isNoProgram(initialValues.program));
  const subscriptionsChanged =
    values.subscriptionsEnabled !== initialValues.subscriptionsEnabled;
  const fatcaCrsChanged = values.fatcaCrsEnabled !== initialValues.fatcaCrsEnabled;

  const changes: ProgramChangeRow[] = [];
  if (programChanged) {
    changes.push({
      label: "Investor AML/KYC compliance program",
      current: initialValues.program || "--",
      next: values.program,
      currentHasWarning: initialValues.program === LEGACY_KYC,
    });
  }
  if (subscriptionsChanged) {
    changes.push({
      label: "Subscriptions integration",
      current: initialValues.subscriptionsEnabled ? "Enabled" : "Disabled",
      next: values.subscriptionsEnabled ? "Enabled" : "Disabled",
    });
  }
  if (fatcaCrsChanged) {
    changes.push({
      label: "FATCA and CRS data collection",
      current: initialValues.fatcaCrsEnabled ? "Enabled" : "Disabled",
      next: values.fatcaCrsEnabled ? "Enabled" : "Disabled",
    });
  }

  const applyChanges = async () => {
    setApplying(true);
    try {
      if (programChanged) {
        const result = await setComplianceProgram({
          variables: {
            arenaId: toGlobalId("ArenaObjectType", row.arenaId),
            entityId: toGlobalId("AccountObjectType", row.id),
            programString: values.program === NONE ? "" : values.program,
          },
        });
        if (!result.data?.setComplianceProgram) return;
      }
      if (subscriptionsChanged && !isNoProgram(values.program)) {
        const result = await enableCompliance({
          variables: {
            arenaId: toGlobalId("ArenaObjectType", row.arenaId),
            entityId: toGlobalId("AccountObjectType", row.id),
            enable: values.subscriptionsEnabled,
          },
        });
        if (!result.data?.enableCompliance) return;
      }
      if (fatcaCrsChanged) {
        const result = await setFatcaCrsDataCollection({
          variables: {
            arenaId: toGlobalId("ArenaObjectType", row.arenaId),
            entityId: toGlobalId("AccountObjectType", row.id),
            enabled: values.fatcaCrsEnabled,
          },
        });
        if (!result.data?.setFatcaCrsDataCollection) return;
      }
      // The modal stays open on failure so the user can retry or cancel
      setShowConfirm(false);
      toast.success(`Investor program updated for ${row.name}`);
      onSaved();
    } finally {
      setApplying(false);
    }
  };

  return (
    <Flex flexDirection="column" gap={Sizes.LG}>
      <Card isFullWidth>
        <Flex flexDirection="column" gap={20}>
          <Text variant="upper-md">Investor AML/KYC compliance program</Text>
          <Text>
            Configure the compliance program that investors must comply with when subscribing
            into {row.name}.
          </Text>
          <div className={drawerStyles.programSelect}>
            <FormSelect
              name="program"
              label="Compliance program"
              placeholder="Select a compliance program"
              full
            >
              <FormSelect.Section>
                {programOptions.map((program) => (
                  <FormSelect.Option key={program} value={program}>
                    {program}
                  </FormSelect.Option>
                ))}
              </FormSelect.Section>
            </FormSelect>
          </div>
          {isOnLegacyKyc(row) && (
            <InfoBox variant="info" title={LEGACY_KYC_ALERT_TITLE}>
              {LEGACY_KYC_ALERT}
            </InfoBox>
          )}
          <Flex justifyContent="space-between" alignItems="center" gap={Sizes.MD}>
            <Flex flexDirection="column">
              <Text variant="strong-md">Subscriptions integration</Text>
              <Text variant="secondary-md">
                This will enable the integrated compliance workflow within digital
                subscriptions.
              </Text>
            </Flex>
            <SubscriptionsToggle
              disabled={isNoProgram(values.program) || values.program === LEGACY_KYC}
              tooltipContent={
                values.program === LEGACY_KYC
                  ? SUBSCRIPTIONS_LEGACY_KYC_TOOLTIP
                  : SUBSCRIPTIONS_DISABLED_TOOLTIP
              }
            />
          </Flex>
        </Flex>
      </Card>

      <Card isFullWidth>
        <Flex flexDirection="column" gap={20}>
          <Text variant="upper-md">FATCA and CRS program</Text>
          <Flex justifyContent="space-between" alignItems="center" gap={Sizes.MD}>
            <Flex flexDirection="column">
              <Text variant="strong-md">FATCA and CRS data collection</Text>
              <Text variant="secondary-md">
                This will enable FATCA and CRS tax data collection for {row.name}.
              </Text>
            </Flex>
            <FormSwitch
              name="fatcaCrsEnabled"
              label="Enabled"
              uncheckedLabel="Disabled"
              data-testid="fatca-crs-enabled-switch"
            />
          </Flex>
        </Flex>
      </Card>

      <Flex justifyContent="flex-end" gap={10} className={drawerStyles.footerActions}>
        <Button variant="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => setShowConfirm(true)}
          disabled={!dirty || changes.length === 0}
          data-testid="investor-program-save-button"
        >
          Save
        </Button>
      </Flex>

      <ChangeProgramsModal
        show={showConfirm}
        title="Change investor programs"
        description={
          <>
            Review the changes to the investor programs for{" "}
            <Text asSpan variant="strong-md">
              {row.name}
            </Text>
            :
          </>
        }
        changes={changes}
        warning={
          programChanged && initialValues.program === LEGACY_KYC
            ? LEGACY_KYC_WARNING
            : undefined
        }
        applying={applying}
        onCancel={() => setShowConfirm(false)}
        onApply={applyChanges}
      />
    </Flex>
  );
};

export const InvestorProgramTab = (props: SettingsDrawerTabProps) => {
  const { row } = props;
  const programsResult = useGetProgramsQuery();

  const programOptions = useMemo(() => {
    const names = (programsResult.data?.programsAssignableToEntities ?? [])
      .map((program) => program?.name)
      .filter((name): name is string => !!name && !EXCLUDED_PROGRAMS.includes(name));
    // Legacy KYC is not assignable, but a legacy entity starts on it
    return [NONE, ...(isOnLegacyKyc(row) ? [LEGACY_KYC] : []), ...names];
  }, [programsResult.data?.programsAssignableToEntities, row]);

  const initialValues: FormValues = {
    program: row.complianceProgram || (isOnLegacyKyc(row) ? LEGACY_KYC : ""),
    subscriptionsEnabled: !!row.complianceEnabled,
    fatcaCrsEnabled: !!row.fatcaCrsEnabled,
  };

  return (
    <Form<FormValues>
      key={row.id}
      initialValues={initialValues}
      onSubmit={() => {
        // Saves run through the confirmation modal's Apply, not Formik submit
      }}
    >
      <InvestorProgramForm
        {...props}
        programOptions={programOptions}
        initialValues={initialValues}
      />
    </Form>
  );
};
